import { Shape } from '../types';

const SNAP_THRESHOLD = 8;

interface SnapResult {
  position: { x?: number; y?: number };
  lines: { x?: number; y?: number };
}

export const getSnapPosition = (
  activeShape: Shape,
  otherShapes: Shape[],
  position: { x: number; y: number },
  skipId?: string
): SnapResult => {
  const result: SnapResult = {
    position: {},
    lines: {},
  };

  // Points to check for the active shape
  const activePoints = {
    left: position.x,
    right: position.x + activeShape.width,
    center: position.x + activeShape.width / 2,
    top: position.y,
    bottom: position.y + activeShape.height,
    middle: position.y + activeShape.height / 2,
  };

  // Collect all snap points from other shapes
  const snapPoints = otherShapes
    .filter(shape => shape.id !== skipId)
    .flatMap(shape => {
      const points = [];
      // Edges
      points.push(
        { x: shape.x, label: 'left' },
        { x: shape.x + shape.width, label: 'right' },
        { x: shape.x + shape.width / 2, label: 'center' },
      );
      points.push(
        { y: shape.y, label: 'top' },
        { y: shape.y + shape.height, label: 'bottom' },
        { y: shape.y + shape.height / 2, label: 'middle' },
      );
      return points;
    });

  // Check for x-axis snapping
  ['left', 'right', 'center'].forEach(edge => {
    const activeValue = activePoints[edge as keyof typeof activePoints] as number;
    snapPoints
      .filter(point => point.x !== undefined)
      .forEach(point => {
        const diff = Math.abs(point.x! - activeValue);
        if (diff < SNAP_THRESHOLD) {
          const snapX = point.x!;
          let finalX = snapX;
          
          // Adjust based on which edge we're snapping
          if (edge === 'right') {
            finalX = snapX - activeShape.width;
          } else if (edge === 'center') {
            finalX = snapX - activeShape.width / 2;
          }
          
          result.position.x = finalX;
          result.lines.x = snapX;
        }
      });
  });

  // Check for y-axis snapping
  ['top', 'bottom', 'middle'].forEach(edge => {
    const activeValue = activePoints[edge as keyof typeof activePoints] as number;
    snapPoints
      .filter(point => point.y !== undefined)
      .forEach(point => {
        const diff = Math.abs(point.y! - activeValue);
        if (diff < SNAP_THRESHOLD) {
          const snapY = point.y!;
          let finalY = snapY;
          
          // Adjust based on which edge we're snapping
          if (edge === 'bottom') {
            finalY = snapY - activeShape.height;
          } else if (edge === 'middle') {
            finalY = snapY - activeShape.height / 2;
          }
          
          result.position.y = finalY;
          result.lines.y = snapY;
        }
      });
  });

  return result;
};